import basicFields from "./patientFields.json";
import { directoryMembership } from "./patientDirectory";
import { canAccess } from "./policy";
// Public SDK facade: full patient records are projected by the server before reaching a client.
import {
  getDoc as sdkGetDoc,
  onSnapshot as sdkOnSnapshot,
  doc,
  addDoc as sdkAddDoc,
  updateDoc as sdkUpdateDoc,
  setDoc as sdkSetDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
export * from "firebase/firestore";
const patientPath = (reference) =>
  reference?.type === "document" &&
  /^clinics\/[^/]+\/pacientes\/[^/]+$/.test(reference.path);
async function projectedPatient(reference) {
  const [, clinicId, , patientId] = reference.path.split("/");
  const result = await httpsCallable(
    functions,
    "getAccessiblePatient",
  )({ clinicId, patientId });
  return {
    id: reference.id,
    ref: reference,
    exists: () => result.data.exists === true,
    data: () => result.data.patient,
  };
}
export function getDoc(reference) {
  return patientPath(reference)
    ? projectedPatient(reference)
    : sdkGetDoc(reference);
}
export function onSnapshot(reference, ...args) {
  if (!patientPath(reference)) return sdkOnSnapshot(reference, ...args);
  const next = typeof args[0] === "function" ? args[0] : args[0]?.next,
    error = typeof args[1] === "function" ? args[1] : args[0]?.error;
  const [, clinicId, , patientId] = reference.path.split("/");
  let live = true,
    revision = 0;
  const unsubscribe = sdkOnSnapshot(
    doc(reference.firestore, "clinics", clinicId, "patientIndex", patientId),
    () => {
      const current = ++revision;
      projectedPatient(reference).then(
        (snapshot) => {
          if (live && current === revision) next?.(snapshot);
        },
        (caught) => {
          if (live && current === revision) error?.(caught);
        },
      );
    },
    error,
  );
  return () => {
    live = false;
    unsubscribe();
  };
}

function patientPayload(reference, data, action) {
  if (!/^clinics\/[^/]+\/pacientes(?:\/[^/]+)?$/.test(reference?.path || ""))
    return data;
  const clinicId = reference.path.split("/")[1],
    member = directoryMembership(clinicId);
  if (!member) return data;
  const metadata = [
    "createdAt",
    "createdBy",
    "createdByName",
    "createdByEmail",
    "updatedAt",
    "updatedBy",
    "updatedByName",
    "updatedByEmail",
  ];
  const modules = {
    odontograma: "odontogram",
    odontogram: "odontogram",
    periodonto: "periodonto",
    periodontograma: "periodonto",
    fotos: "photos",
    sesiones: "photos",
    sesionesFotos: "photos",
    bolton: "bolton",
    saldoPendiente: "finance",
    estadoFinanciero: "finance",
    historiaClinica: "history",
  };
  return Object.fromEntries(
    Object.entries(data).filter(([key, value]) => {
      if (basicFields.includes(key) || metadata.includes(key)) return true;
      const module = key.startsWith("ortodoncia.cefalometria")
        ? "ceph"
        : modules[key.split(".")[0]] || "history";
      if (canAccess(member, module, action)) return true;
      const empty =
        value == null ||
        value === "" ||
        value === false ||
        (Array.isArray(value) && !value.length);
      if (empty) return false;
      throw new Error(
        "No tienes permiso para modificar esa sección del expediente.",
      );
    }),
  );
}
export function addDoc(reference, data) {
  return sdkAddDoc(reference, patientPayload(reference, data, "create"));
}
export function updateDoc(reference, data) {
  return sdkUpdateDoc(reference, patientPayload(reference, data, "update"));
}
export function setDoc(reference, data, ...options) {
  return sdkSetDoc(
    reference,
    patientPayload(reference, data, "update"),
    ...options,
  );
}
